<template>
  <SliderModalItem>
    <SliderModalContainer
      :modal-title="$t('products.filters.categories')"
      modal-header-animation="teleport"
      modal-close-btn-icon="chevron"
      :modal-clear-btn-text="
        store.selectedCategory ? $t('products.filters.clear') : ''
      "
      @clear="setSelectedCategory('')"
    >
      <template #slider-modal-trigger-btn>
        <SliderModalNavBtn
          :btn-title="$t('products.filters.categories')"
          :btn-text="formattedSelection"
        />
      </template>

      <template #slider-modal-content>
        <MwebCategoryTree
          :category-tree="categoryTree"
          :formatted-selection="formattedSelection"
        />
      </template>
    </SliderModalContainer>
  </SliderModalItem>
</template>

<script lang="ts" setup>
import { type CategoryFilters } from '~/types/api/data/product/filter/filter.types'
import SliderModalItem from './slider-modal-item.vue'
import SliderModalContainer from './slider-modal-container.vue'
import SliderModalNavBtn from './slider-modal-nav-btn.vue'
import MwebCategoryTree from './category-tree.vue'

interface CategoryTree extends CategoryFilters {
  children: CategoryTree[]
}

const props = defineProps<{
  categoryFilters: CategoryFilters[]
}>()
const store = useProductListingFiltersStore() // Desctructuring from store breaks top-level refs
const { setSelectedCategory } = store

/**
 * ----- States -----
 */
const categoryTree: Ref<CategoryTree[]> = ref([])
const slugToNameMap: Ref<{ [key: string]: string }> = ref({})

/**
 * ----- Watchers/Lifecycle -----
 */
onMounted(() => {
  initRefs()
})
watch(
  () => props.categoryFilters,
  () => {
    initRefs()
  }
)

/**
 * ----- Computed -----
 */
const formattedSelection = computed(() => {
  return slugToNameMap.value[store.selectedCategory]
})

/**
 * ----- Methods -----
 */
function initRefs() {
  categoryTree.value = catsInTree(props.categoryFilters)

  slugToNameMap.value = props.categoryFilters.reduce(
    (obj: { [key: string]: string }, cat) => {
      obj[cat['slugUrl']] = cat['label']
      return obj
    },
    {}
  )
}

function catsInTree(
  categories: CategoryFilters[],
  parentId: string | null = null
) {
  const currentCats: CategoryTree[] = []

  categories.forEach(cat => {
    const catParentId = cat['parentId'] ? cat['parentId'].toString() : null

    if (catParentId === parentId) {
      const newCat = {
        ...cat,
        children: catsInTree(categories, cat['id']),
      }
      currentCats.push(newCat)
    }
  })

  return currentCats
}
</script>

<style lang="scss" scoped></style>
